import type { definitions } from '@luxuryescapes/contract-trip'
import type { SavedCruiseData } from 'tripPlanner/types/tripItem'
import { isRecord } from 'lib/object/objectUtils'

// Derive savedItemData directly from the dayTripItem schema definition.
// This guarantees the full source union ('offer' | 'spp' | ...) regardless
// of how deeply-nested path-indexed types resolve in different TS environments.
type DayTripSavedItemData = NonNullable<definitions['dayTripItem']['savedItemData']>

type ContractOfferSavedData = Extract<DayTripSavedItemData, { source: 'offer' }>
type ContractSppSavedData = Extract<DayTripSavedItemData, { source: 'spp' }>
type SavedHotelRateData = { rateId?: string }

export type OfferSavedData = ContractOfferSavedData & SavedHotelRateData
export type SppSavedData = ContractSppSavedData & SavedHotelRateData
export type ExperienceSavedData = Extract<DayTripSavedItemData, { source: 'offer_experience' }>
export type HotelSavedData = OfferSavedData | SppSavedData
export type CruiseSavedData = Extract<SavedCruiseData, { offerType: 'cruise' }>

const HOTEL_SOURCES: ReadonlySet<string> = new Set(['offer', 'spp'])

export function isHotelSavedData(
  data: DayTripSavedItemData | null | undefined,
): data is HotelSavedData {
  return data != null && HOTEL_SOURCES.has(data.source)
}

export function isSppSavedData(data: HotelSavedData): data is SppSavedData {
  return data.source === 'spp'
}

export function isExperienceSavedData(
  data: DayTripSavedItemData | null | undefined,
): data is ExperienceSavedData {
  return data?.source === 'offer_experience'
}

export function isCruiseSavedData(data: unknown): data is CruiseSavedData {
  return isRecord(data) &&
    data.offerType === 'cruise' &&
    typeof data.offerId === 'string'
}

export function getCruiseSavedOfferId(data: unknown): string | undefined {
  return isCruiseSavedData(data) ? data.offerId : undefined
}
